import { useEffect, useMemo, useState } from "react";
import { useTheme } from "next-themes";
import { DiffCodeEditor, type MonacoEditorTheme } from "@ncoder/code-editor";
import { apiBlob } from "@/services/studio/client";
import { agentArtifactUrl, artifactUrl } from "@/lib/studio/runs";
import type { RunArtifact } from "@/services/studio/types";
import { SkillResultMarkdown } from "./SkillResultMarkdown";

interface InlineArtifactPreviewProps {
  runId: string;
  artifacts: RunArtifact[];
  orgId?: string | null;
  sourceSurface: "agent" | "playbook";
  /**
   * Override the artifact-body URL. The migrations engine serves its artifacts from a
   * different route (`/api/migrations/plans/{id}/tasks/{key}/artifacts/{component}`)
   * than the two run surfaces, and everything else here — the auth'd blob fetch, the
   * size caps, the fence/diff/PDF routing — is identical, so the only thing worth
   * varying is where the bytes come from. Omitted, behaviour is exactly as before.
   */
  resolveUrl?: (name: string) => string;
}

interface LoadedArtifact {
  artifact: RunArtifact;
  content: string;
}

interface LoadedPdf {
  artifact: RunArtifact;
  url: string;
}

const MAX_INLINE_FILES = 8;
const MAX_INLINE_BYTES = 120_000;
// PDFs are binary — they render in a native-viewer <iframe> (object URL), NOT as text,
// so they get a far larger cap than the text-fence path. A generated release/report PDF
// is typically a few MB; 25 MB is a safe ceiling before we fall back to download-only.
const MAX_INLINE_PDF_BYTES = 25_000_000;

export function InlineArtifactPreview({ runId, artifacts, orgId, sourceSurface, resolveUrl }: InlineArtifactPreviewProps) {
  const { resolvedTheme } = useTheme();
  const inlineArtifacts = useMemo(() => artifacts.filter(isInlineGeneratedArtifact).slice(0, MAX_INLINE_FILES), [artifacts]);
  const sourceArtifacts = useMemo(() => artifacts.filter(isSourceArtifact).slice(0, MAX_INLINE_FILES), [artifacts]);
  const pdfArtifacts = useMemo(() => artifacts.filter(isInlinePdfArtifact).slice(0, MAX_INLINE_FILES), [artifacts]);
  const [loaded, setLoaded] = useState<LoadedArtifact[]>([]);
  const [loadedSources, setLoadedSources] = useState<LoadedArtifact[]>([]);
  const [loadedPdfs, setLoadedPdfs] = useState<LoadedPdf[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  const editorTheme = (resolvedTheme === "dark" ? "vs-dark" : "vs") as MonacoEditorTheme;

  useEffect(() => {
    let cancelled = false;
    const objectUrls: string[] = [];
    setLoaded([]);
    setLoadedSources([]);
    setLoadedPdfs([]);
    setFailed(null);
    // When orgId is null the artifact URLs would 404 (org context required by
    // the backend). For migrations, orgId arrives with the persisted run fetch
    // which lags a few seconds behind the SSE 'artifact' events. Wait until
    // it's available so we don't flash a load-error state right after the run.
    if (!runId || !orgId || (inlineArtifacts.length === 0 && sourceArtifacts.length === 0 && pdfArtifacts.length === 0)) return;

    const urlFor = (name: string) =>
      resolveUrl
        ? resolveUrl(name)
        : sourceSurface === "agent"
          ? agentArtifactUrl(runId, name, orgId)
          : artifactUrl(runId, name, orgId);

    const load = async () => {
      try {
        const rows = await Promise.all(
          [...sourceArtifacts, ...inlineArtifacts].map(async (artifact) => {
            const { blob } = await apiBlob(urlFor(artifact.name));
            return { artifact, content: await blob.text() };
          })
        );
        // PDFs are binary: keep the blob and expose a native-viewer object URL. NEVER
        // .text() them — that's what turned PDFs into a wall of garbled bytes before.
        const pdfs = await Promise.all(
          pdfArtifacts.map(async (artifact) => {
            const { blob } = await apiBlob(urlFor(artifact.name));
            // Force the PDF MIME type. Artifact endpoints often return
            // application/octet-stream, and a blob: URL carries no extension — so
            // without an explicit application/pdf type the browser may render only
            // the first page (or offer a download) instead of the full multi-page
            // viewer. blob.slice keeps the bytes and just retags the type.
            const pdfBlob = blob.type === "application/pdf" ? blob : blob.slice(0, blob.size, "application/pdf");
            const url = URL.createObjectURL(pdfBlob);
            objectUrls.push(url);
            return { artifact, url };
          })
        );
        if (!cancelled) {
          setLoaded(rows.filter((row) => isInlineGeneratedArtifact(row.artifact)));
          setLoadedSources(rows.filter((row) => isSourceArtifact(row.artifact)));
          setLoadedPdfs(pdfs);
        } else {
          objectUrls.forEach((url) => URL.revokeObjectURL(url));
        }
      } catch (error) {
        if (!cancelled) setFailed(error instanceof Error ? error.message : "Could not load generated files");
      }
    };

    void load();
    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [inlineArtifacts, orgId, runId, sourceArtifacts, sourceSurface, pdfArtifacts, resolveUrl]);

  if (inlineArtifacts.length === 0 && pdfArtifacts.length === 0) return null;

  // Waiting for orgId (arrives with the persisted run a few seconds after SSE events)
  if (!orgId) {
    return <p className="mt-4 font-mono text-xs text-muted-foreground">Loading generated files…</p>;
  }

  if (loaded.length === 0 && loadedPdfs.length === 0 && !failed) {
    return <p className="mt-4 font-mono text-xs text-muted-foreground">Loading generated files…</p>;
  }

  if (failed) {
    return <p className="mt-4 font-mono text-xs text-destructive">Generated files could not be loaded inline: {failed}</p>;
  }

  return (
    <div className="mt-5 border-t border-border pt-4">
      <GeneratedDiffPreview generated={loaded} sources={loadedSources} theme={editorTheme} />
      {loaded.length > 0 ? (
        <>
          <p className="mb-3 font-mono text-[11px] uppercase text-muted-foreground">Generated Files</p>
          <SkillResultMarkdown content={buildPreviewMarkdown(loaded)} isStreaming={false} />
        </>
      ) : null}
      {loadedPdfs.length > 0 ? (
        <div className={loaded.length > 0 ? "mt-6 space-y-4" : "space-y-4"}>
          <p className="mb-1 font-mono text-[11px] uppercase text-muted-foreground">PDF Documents</p>
          {loadedPdfs.map(({ artifact, url }) => (
            <div key={`pdf-${artifact.name}`} className="overflow-hidden rounded-lg border border-border bg-card">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/35 px-3 py-2">
                <p className="truncate font-mono text-xs text-foreground">{artifact.name}</p>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 font-mono text-[11px] text-[hsl(var(--accent-warm))] hover:underline"
                >
                  Open in new tab ↗
                </a>
              </div>
              {/* Native browser PDF viewer via same-origin blob object URL — renders ALL
                  pages, scrollable (FitH = fit width). If a strict CSP ever blocks the
                  frame, the "Open in new tab" link above still works. */}
              <iframe
                title={artifact.name}
                src={`${url}#toolbar=1&navpanes=0&view=FitH`}
                className="h-[640px] w-full bg-white"
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function GeneratedDiffPreview({
  generated,
  sources,
  theme,
}: {
  generated: LoadedArtifact[];
  sources: LoadedArtifact[];
  theme: MonacoEditorTheme;
}) {
  const diffRows = generated.filter((row) => isDiffableGeneratedArtifact(row.artifact));
  if (diffRows.length === 0) return null;
  const source = bestSourceArtifact(sources);
  return (
    <div className="mb-5 space-y-4">
      <p className="font-mono text-[11px] uppercase text-muted-foreground">Refactor Proposal Diff</p>
      {diffRows.map((row) => {
        const baseline = source && hasRealSourceBody(source.content) ? source.content : "";
        const isNewFile = !baseline;
        const height = Math.min(Math.max(row.content.split("\n").length * 18 + 96, 260), 640);
        return (
          <div key={`diff-${row.artifact.name}`} className="overflow-hidden rounded-lg border border-border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/35 px-3 py-2">
              <div className="min-w-0">
                <p className="truncate font-mono text-xs text-foreground">{row.artifact.name}</p>
                <p className="font-mono text-[11px] text-muted-foreground">
                  {isNewFile
                    ? "Original source body was not available; showing generated file as a new-file diff."
                    : `Compared with ${source?.artifact.name}`}
                </p>
              </div>
              <span className="rounded bg-background px-2 py-1 font-mono text-[10px] uppercase text-muted-foreground">
                {isNewFile ? "new file" : "diff"}
              </span>
            </div>
            <div style={{ height }}>
              <DiffCodeEditor
                original={baseline}
                modified={row.content}
                language={languageForArtifact(row.artifact.name, row.artifact.content_type)}
                theme={theme}
                viewMode="inline"
                readOnly
                inlineActions={false}
                editorOptions={{ ignoreTrimWhitespace: false }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function buildPreviewMarkdown(rows: LoadedArtifact[]): string {
  return rows
    .map(({ artifact, content }) => {
      const language = languageForArtifact(artifact.name, artifact.content_type);
      return `### ${artifact.name}\n\n\`\`\`${language} filename=${artifact.name}\n${fenceSafe(content)}\n\`\`\``;
    })
    .join("\n\n");
}

function fenceSafe(content: string): string {
  return content.replace(/```/g, "``\\`");
}

function isInlineGeneratedArtifact(artifact: RunArtifact): boolean {
  if (!artifact.name || artifact.size_bytes > MAX_INLINE_BYTES) return false;
  const name = artifact.name.toLowerCase();
  if (name.includes("_evidence") || name.includes("_share_context") || name.includes("_full_run_metadata")) return false;
  if (name.includes("_action_manifest") || name.includes("_steps") || name.includes("_findings")) return false;
  if (name.includes("_prompt") || name.includes("_full_source") || name.includes("_source_")) return false;
  if (/\.(cls|trigger|soql|apex|xml|js|ts|tsx|jsx|css|html|json|yaml|yml)$/i.test(artifact.name)) return true;
  return /(?:notes|generation_notes|validation_notes)\.md$/i.test(artifact.name);
}

function isInlinePdfArtifact(artifact: RunArtifact): boolean {
  if (!artifact.name) return false;
  return artifact.name.toLowerCase().endsWith(".pdf") && artifact.size_bytes <= MAX_INLINE_PDF_BYTES;
}

function isSourceArtifact(artifact: RunArtifact): boolean {
  const name = artifact.name.toLowerCase();
  return /_source_\d+_/.test(name) && /\.(cls|trigger|apex|xml|js|ts|tsx|jsx|html|css|json|md)$/i.test(artifact.name);
}

function isDiffableGeneratedArtifact(artifact: RunArtifact): boolean {
  if (!/\.(cls|trigger|apex|xml|js|ts|tsx|jsx|html|css|page|component|flow)$/i.test(artifact.name)) return false;
  const lower = artifact.name.toLowerCase();
  // Exclude meta XMLs (they're tiny config wrappers, no useful diff signal).
  if (lower.endsWith("-meta.xml") || lower.endsWith(".cls-meta.xml") || lower.endsWith(".trigger-meta.xml") || lower.endsWith(".js-meta.xml")) {
    return false;
  }
  // Every other code artifact gets the diff view. The DiffCodeEditor falls back
  // to a "new file" diff when no matching source exists, which is still useful.
  return true;
}

function bestSourceArtifact(sources: LoadedArtifact[]): LoadedArtifact | null {
  return sources.find((source) => hasRealSourceBody(source.content)) ?? sources[0] ?? null;
}

function hasRealSourceBody(content: string): boolean {
  const trimmed = content.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("# ") && trimmed.includes("Content source: graph summary")) return false;
  if (trimmed.includes("full source body was not present")) return false;
  return /(?:public|private|global|trigger|class|interface)\s+\w+/.test(trimmed) || trimmed.split("\n").length > 20;
}

function languageForArtifact(name: string, contentType?: string): string {
  const lower = name.toLowerCase();
  if (lower.endsWith(".cls") || lower.endsWith(".trigger") || lower.endsWith(".apex")) return "apex";
  if (lower.endsWith(".xml")) return "xml";
  if (lower.endsWith(".soql")) return "soql";
  if (lower.endsWith(".json") || contentType?.includes("json")) return "json";
  if (lower.endsWith(".md")) return "markdown";
  if (lower.endsWith(".yaml") || lower.endsWith(".yml")) return "yaml";
  if (lower.endsWith(".ts") || lower.endsWith(".tsx")) return "typescript";
  if (lower.endsWith(".js") || lower.endsWith(".jsx")) return "javascript";
  if (lower.endsWith(".css")) return "css";
  if (lower.endsWith(".html")) return "html";
  return "text";
}
